// src/features/tournament/services/tournamentService.ts

import api from '../../../api/axios';

import {
  ADMIN_USER_ENDPOINTS,
  TOURNAMENT_ENDPOINTS,
} from '../../../api';

import type {
  ApiResponse,
} from '../../../shared/types/api.types';

import type {
  MyTournamentReservation,
  TournamentAdminParticipantRequest,
  TournamentCreateRequest,
  TournamentListItem,
  TournamentParticipantsData,
  TournamentReserveResponse,
  UserSearchField,
  UserSearchItem,
} from '../types/tournament.types';


/**
 * =========================================================
 * ID 검증
 * =========================================================
 */
const validateId = (
  value: number,
  name: string,
): void => {
  if (
    !Number.isInteger(value) ||
    value <= 0
  ) {
    throw new Error(
      `올바른 ${name}가 필요합니다.`,
    );
  }
};


/**
 * =========================================================
 * 단건 응답 데이터 검증
 * =========================================================
 */
const requireData = <T>(
  data: T | null | undefined,
  message: string,
): T => {
  if (
    data === null ||
    data === undefined
  ) {
    throw new Error(message);
  }

  return data;
};


/**
 * =========================================================
 * 배열 응답 안전 처리
 * =========================================================
 */
const getArrayData = <T>(
  data:
    | T[]
    | null
    | undefined,
): T[] => {
  return Array.isArray(data)
    ? data
    : [];
};


/**
 * =========================================================
 * 대회 생성
 *
 * SUPERADMIN
 * =========================================================
 */
export const createTournament =
  async (
    request:
      TournamentCreateRequest,
  ): Promise<void> => {
    await api.post(
      TOURNAMENT_ENDPOINTS
        .SUPERADMIN
        .CREATE,
      request,
    );
  };


/**
 * =========================================================
 * 대회 목록 조회
 *
 * USER / ADMIN / SUPERADMIN
 *
 * month:
 * yyyy-MM
 *
 * date:
 * yyyy-MM-dd
 *
 * date가 존재하면
 * 백엔드에서 date 조건을 우선합니다.
 * =========================================================
 */
export const getTournaments =
  async (
    month?: string,
    date?: string,
  ): Promise<
    TournamentListItem[]
  > => {
    const response =
      await api.get<
        ApiResponse<
          TournamentListItem[]
        >
      >(
        TOURNAMENT_ENDPOINTS.LIST,
        {
          params: {
            month:
              month ||
              undefined,

            date:
              date ||
              undefined,
          },
        },
      );


    return getArrayData(
      response.data.data,
    );
  };


/**
 * =========================================================
 * 대회 참가자 목록 조회
 *
 * SUPERADMIN
 *
 * 동일 회원이 여러 번 등록된 경우
 * participantId가 서로 다른
 * 별도의 엔트리로 반환됩니다.
 * =========================================================
 */
export const getTournamentParticipants =
  async (
    tournamentId: number,
  ): Promise<
    TournamentParticipantsData
  > => {
    validateId(
      tournamentId,
      '대회 ID',
    );


    const response =
      await api.get<
        ApiResponse<
          TournamentParticipantsData
        >
      >(
        TOURNAMENT_ENDPOINTS
          .SUPERADMIN
          .PARTICIPANTS(
            tournamentId,
          ),
      );


    return requireData(
      response.data.data,
      '대회 참가자 정보를 확인할 수 없습니다.',
    );
  };


/**
 * =========================================================
 * 대회 참가자 직접 추가
 *
 * SUPERADMIN
 *
 * 동일 회원을 여러 번 추가할 수 있습니다.
 *
 * 동일 회원이 다시 추가되면
 * 각각 별도의 participantId가 생성됩니다.
 * =========================================================
 */
export const addTournamentParticipant =
  async (
    tournamentId: number,
    request:
      TournamentAdminParticipantRequest,
  ): Promise<
    TournamentReserveResponse
  > => {
    validateId(
      tournamentId,
      '대회 ID',
    );


    const response =
      await api.post<
        ApiResponse<
          TournamentReserveResponse
        >
      >(
        TOURNAMENT_ENDPOINTS
          .SUPERADMIN
          .PARTICIPANTS(
            tournamentId,
          ),
        request,
      );


    return requireData(
      response.data.data,
      '대회 참가자 추가 결과를 확인할 수 없습니다.',
    );
  };


/**
 * =========================================================
 * 참가 엔트리 삭제
 *
 * SUPERADMIN
 *
 * userId 기준이 아니라
 * participantId 기준으로 삭제합니다.
 *
 * 동일 회원이 여러 엔트리를 가지고 있어도
 * 지정된 엔트리 하나만 삭제됩니다.
 * =========================================================
 */
export const removeTournamentParticipant =
  async (
    tournamentId: number,
    participantId: number,
  ): Promise<void> => {
    validateId(
      tournamentId,
      '대회 ID',
    );


    validateId(
      participantId,
      '참가 엔트리 ID',
    );


    await api.delete(
      TOURNAMENT_ENDPOINTS
        .SUPERADMIN
        .PARTICIPANT(
          tournamentId,
          participantId,
        ),
    );
  };


/**
 * =========================================================
 * USER 본인 예약 대회 목록 조회
 *
 * 이미 참가자 목록에 등록된 대회를 조회합니다.
 *
 * USER
 *
 * GET
 * /api/v2/users/me/tournaments/reservations
 * =========================================================
 */
export const getMyTournamentReservations =
  async (): Promise<
    MyTournamentReservation[]
  > => {

    const response =
      await api.get<
        ApiResponse<
          MyTournamentReservation[]
        >
      >(
        TOURNAMENT_ENDPOINTS
          .MY_RESERVATIONS,
      );


    return getArrayData(
      response.data.data,
    );
  };  

/**
 * =========================================================
 * USER 본인 대회 예약
 *
 * USER
 *
 * JWT 로그인 사용자를 기준으로
 * 본인의 참가 엔트리를 생성합니다.
 * =========================================================
 */
export const reserveTournament =
  async (
    tournamentId: number,
  ): Promise<
    TournamentReserveResponse
  > => {
    validateId(
      tournamentId,
      '대회 ID',
    );


    const response =
      await api.post<
        ApiResponse<
          TournamentReserveResponse
        >
      >(
        TOURNAMENT_ENDPOINTS
          .RESERVE(
            tournamentId,
          ),
      );


    return requireData(
      response.data.data,
      '대회 예약 결과를 확인할 수 없습니다.',
    );
  };


/**
 * =========================================================
 * 참가자 추가용 회원 검색
 *
 * ADMIN / SUPERADMIN
 *
 * GET /api/v2/admin/users/search
 *
 * 검색 가능 필드:
 * - userId
 * - userName
 * - userNickname
 * - phoneNumber
 *
 * 예:
 *
 * searchTournamentUsers(
 *   'userNickname',
 *   '라포이트',
 * )
 *
 * =>
 *
 * GET
 * /api/v2/admin/users/search
 * ?userNickname=라포이트
 * =========================================================
 */
export const searchTournamentUsers =
  async (
    field:
      UserSearchField,
    keyword: string,
  ): Promise<
    UserSearchItem[]
  > => {
    const normalizedKeyword =
      keyword.trim();


    if (
      !normalizedKeyword
    ) {
      return [];
    }


    const response =
      await api.get<
        ApiResponse<
          UserSearchItem[]
        >
      >(
        ADMIN_USER_ENDPOINTS
          .SEARCH,
        {
          params: {
            [field]:
              normalizedKeyword,
          },
        },
      );


    return getArrayData(
      response.data.data,
    );
  };